/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    heading: string;
    viewAllUrl?: string;
}
export interface AddToListModalStyles {
    gridContainerStyle?: GridContainerProps;
    titledWrapperStyle?: InjectableCss;
    typographyStyle?: TypographyProps;
    linkStyle?: LinkPresentationProps;
    iconStyle?: IconPresentationProps;
}

export const addToListModalStyles: AddToListModalStyles = {
    gridContainerStyle: {
        offsetCss: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-top: 40px;
            &&& {
                width: 100%;
                margin: 0;
            }
        `,
    },
    titledWrapperStyle: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
            width: 100%;
        `,
    },
    typographyStyle: {
        as: "h3",
        variant: "h3",
        css: css`
            margin-bottom: 0;
            text-transform: capitalize;
        `,
    },
    linkStyle: {
        color: "secondary",
        mergeCss: true,
        css: css`
            > span {
                display: flex;
                align-items: center;
                margin-left: 32px;
                font-size: 15px;
                font-weight: bold;
            }
        `,
    },
    iconStyle: {
        src: "ChevronRight",
        size: 14,
        color: "secondary",
        css: css`
            margin-left: 5px;
        `,
    },
};
const styles = addToListModalStyles;

const CardListHeading: React.FunctionComponent<OwnProps> = ({ heading, viewAllUrl }) => {
    return (
        <GridItem width={12}>
            <GridContainer {...styles.gridContainerStyle}>
                <StyledWrapper {...styles.titledWrapperStyle}>
                    <Typography {...styles.typographyStyle} className="card-list-heading">
                        {heading}
                    </Typography>
                    <Link
                        {...styles.linkStyle}
                        href={viewAllUrl}
                        className="view-all-link"
                        data-test-selector="cardListHeadingViewAllLink"
                    >
                        {translate("View All")}
                        <Icon {...styles.iconStyle}></Icon>
                        <VisuallyHidden>
                            {translate("View All")} {heading}
                        </VisuallyHidden>
                    </Link>
                </StyledWrapper>
            </GridContainer>
        </GridItem>
    );
};

export default CardListHeading;
